import React, { useState } from 'react'
import { v4 } from 'uuid'
import { Transforms, Range, Editor } from 'slate'
import { ReactEditor, useSlateStatic } from 'slate-react'
import {
  cbModal,
  ModalBody,
  ModalHeader,
  Button,
  Icon,
  Field,
  EditableTags,
  ModalFooter,
  Pills
} from '../../../../../../index'
import { Registry } from '@react-registry'

import { setElementClass, setElementId, getElementClass, getElementId, getInlineClass, getInlineId, setInlineClass, setInlineId } from './utils'
import { setOldSelection } from '../../../utils/setOldSelection';
import { elementCategories } from '../elementCategories'
import { IPill } from '../../../../../Pills/type'

export function openPropertyModal(event, editor) {
  event?.preventDefault()
  if (editor.selection) {
    const [start, end] = Range.edges(editor.selection);
    const range = Editor.range(editor, start, end);
    const selectedText = Editor.string(editor, range);

    const isInline = selectedText.length > 0;

    let initialClassName;
    let initialId;

    if (isInline) {
      initialClassName = getInlineClass(editor, editor.selection)
      initialId = getInlineId(editor, editor.selection)
    } else {
      initialClassName = getElementClass(editor, editor.selection)
      initialId = getElementId(editor, editor.selection)
    }

    editor.setFullScreen();

    cbModal({
      modalProps: {
        shouldReturnFocusAfterClose: false,
        onClose: () => setOldSelection(editor),
      },
      component: (props) => (
        <PropertyOverlayModal
          {...props}
          editor={editor}
          selection={editor.selection}
          initialClassName={initialClassName}
          initialId={initialId}
          isInline={isInline}
        />
      )
    })
  }
}

export function PropertyOverlayWrapper(props) {
  const editor = useSlateStatic()
  const handleMouseDown = (event) => {
    openPropertyModal(event, editor)
  }

  return <React.Fragment>{props.children({ handleMouseDown })}</React.Fragment>
}

export function splitItemsOnSpace(items: IPill[]) {
  const newItems = items.flatMap((item) => {
    if (item.text.search(" ") === -1) return [item]
    return item.text.split(" ").map((text) => ({ text, id: v4().split('-').join('') }))
  })
  return newItems
}

export function PropertyOverlayModal(props) {
  const { selection, initialClassName, initialId, editor } = props
  const [classVal, setClassVal] = useState(initialClassName || '')
  const [idVal, setIdVal] = useState(initialId || '')
  const [applied, setApplied] = useState(false)
  const finalSelection = Editor.rangeRef(editor, selection)

  // class function
  const handleClassPlace = () => {
    Transforms.select(editor, finalSelection.current)
    if (props.isInline) {
      setInlineClass(editor, classVal, finalSelection.current)
    } else {
      setElementClass(editor, classVal, finalSelection.current)
    }
  }

  const handleClassClear = () => {
    Transforms.select(editor, finalSelection.current)
    setClassVal('')
    setClassValPill([])
    if (props.isInline) {
      setInlineClass(editor, '', finalSelection.current)
    } else {
      setElementClass(editor, '', finalSelection.current)
    }
  }

  // Id functions
  const handleIdPlace = () => {
    Transforms.select(editor, finalSelection.current)
    if (props.isInline) {
      setInlineId(editor, idVal, finalSelection.current)
    } else {
      setElementId(editor, idVal, finalSelection.current)
    }
  }

  const handleIdClear = () => {
    Transforms.select(editor, finalSelection.current)
    setIdVal('')
    setIdValPill([])
    if (props.isInline) {
      setInlineId(editor, '', finalSelection.current)
    } else {
      setElementId(editor, '', finalSelection.current)
    }
  }

  const handleClear = () => {
    handleClassClear()
    handleIdClear()
    setApplied(false)
  }

  const handleApply = () => {
    handleClassPlace()
    handleIdPlace()
    setApplied(true)
    closeModal()
  }

  const closeModal = () => {
    props.closeModal()
    ReactEditor.focus(editor)
    Transforms.select(editor, finalSelection.current)
    finalSelection.unref()
  }

  const [classValPill, setClassValPill] = useState(() => {
    return classVal && classVal.length > 0 ? classVal.split(' ').map(text => ({ text, id: v4().split('-').join('') })) : []
  })

  const [idValPill, setIdValPill] = useState(() => {
    return idVal && idVal.length > 0 ? idVal.split(' ').map(text => ({ text, id: v4().split('-').join('') })) : []
  })

  return (
    <div>
      <ModalHeader title="Add Property" closeModal={closeModal} />

      <ModalBody className="modalBodyCustomClass">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Field labelText="Class">
            <Pills
              isEditable={true}
              isSortable={true}
              variant="chip"
              placeholder="Enter Class Property"
              onChange={(changedItems) => {
                const newItems = splitItemsOnSpace(changedItems)
                setClassValPill(newItems)
                setApplied(false)
                setClassVal(newItems.map((item) => item.text).join(' '))
              }}
              testId="property_class_input"
              canTruncate={true}
              isDisabled={false}
              shouldHaveBorder={true}
              items={classValPill}
            />
          </Field>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Field labelText="ID">
            <Pills
              isEditable={true}
              isSortable={true}
              variant="chip"
              placeholder="Enter ID Property"
              onChange={(changedItems) => {
                const newItems = splitItemsOnSpace(changedItems)
                setIdValPill(newItems)
                setApplied(false)
                setIdVal(newItems.map((item) => item.text).join(' '))
              }}
              testId="property_id_input"
              canTruncate={true}
              isDisabled={false}
              shouldHaveBorder={true}
              items={idValPill}
            />
          </Field>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button id="applyBtn" version="v2" onClick={handleApply}>{applied ? 'Applied' : 'Apply'}</Button>
        <span style={{ marginLeft: '16px' }} />
        <Button id="clearBtn" onClick={handleClear} version="v2" buttonType="secondary" disabled={!idVal && !classVal}>
          Clear
        </Button>
        <Button version='v2' onClick={closeModal} buttonType='tertiary'>Cancel</Button>
      </ModalFooter>
    </div>
  )
}

export function shortcutOpenPropertyModal(event, editor, format, registryObj) {
  openPropertyModal(event, editor)
}

Registry.register(
  {
    iconName: <Icon version='v2' icon="Properties" size='medium' />,
    title: 'Add Properties',
    shortcut: { key: 'mod+shift+p', callback: shortcutOpenPropertyModal },
    IngressComponent: PropertyOverlayWrapper,
    category: elementCategories.PROPERTY,
    isContentStackElement: true,
    inBasicToolbar: false,
    canCustomize: false,
  },
  { id: 'property', registry: 'v2.element' }
)